// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the CO-5 loop-aware CLD layout: arrange each feedback
// loop's ordered members into a visibly-closed ring, composing multiple
// interlocking loops so shared members are placed once and linked loops read as
// linked rings. Pure + deterministic, so it unit-tests without a canvas.
//
// The engine hands us each loop's ordered members around the cycle
// (`model.loops()`); this turns those into a position per variable that the CLD
// renders (wiring is a separate step). This is the piece that makes the loops
// *visible as closed cycles by default* -- the whole point of CO-5 -- rather than
// the SFD re-render the CLD is today.
//
// Algorithm: loops are laid out in the given order (rank them first -- dominant
// loop first). A loop that shares no members with anything already placed gets
// its own ring in a left-to-right row. A loop that shares a member rings its
// remaining members OUTWARD from that shared anchor (which stays put and sits on
// the new ring), so the two loops interlock at the shared node. Members are
// placed on the ring in loop order, so following the arrows traces the cycle.

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface LoopForLayout {
  /** Loop id (unused by the math; carried for the caller's convenience). */
  readonly id: string;
  /** Member idents in cycle order. */
  readonly variables: readonly string[];
}

export interface RingLayoutOptions {
  /** Target arc-length between adjacent members on a ring (drives the radius). */
  readonly memberSpacing?: number;
  /** Floor on ring radius, so tiny (1-2 member) loops still read as rings. */
  readonly minRadius?: number;
  /** Horizontal gap between disconnected loop rings. */
  readonly componentGap?: number;
}

const DEFAULT_MEMBER_SPACING = 120;
const DEFAULT_MIN_RADIUS = 90;
const DEFAULT_COMPONENT_GAP = 140;

/** Radius so `count` members sit ~`spacing` apart along the circumference. */
function loopRadius(count: number, spacing: number, minRadius: number): number {
  if (count <= 1) {
    return minRadius;
  }
  return Math.max(minRadius, (spacing * count) / (2 * Math.PI));
}

function dedupeInOrder(idents: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of idents) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function centroid(points: readonly Point[]): Point {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / points.length, y: sy / points.length };
}

/**
 * Compute a ring position for every variable across the given loops. Loops are
 * placed in the order provided (rank them dominant-first before calling). Returns
 * one point per variable; a variable shared by several loops resolves to a single
 * point (the loops interlock there).
 */
export function ringLayout(
  loops: readonly LoopForLayout[],
  options: RingLayoutOptions = {},
): Map<string, Point> {
  const spacing = options.memberSpacing ?? DEFAULT_MEMBER_SPACING;
  const minRadius = options.minRadius ?? DEFAULT_MIN_RADIUS;
  const gap = options.componentGap ?? DEFAULT_COMPONENT_GAP;

  const placed = new Map<string, Point>();
  // Left edge for the next disconnected ring's bounding box.
  let componentX = 0;

  for (const loop of loops) {
    const members = dedupeInOrder(loop.variables);
    const n = members.length;
    if (n === 0) {
      continue;
    }
    const r = loopRadius(n, spacing, minRadius);

    // The first member (in loop order) that is already placed anchors this ring.
    const primaryIndex = members.findIndex((m) => placed.has(m));

    if (primaryIndex < 0) {
      // Disconnected loop: its own ring in the row, first member at the top.
      const center: Point = { x: componentX + r, y: 0 };
      const startAngle = -Math.PI / 2;
      for (let k = 0; k < n; k++) {
        const angle = startAngle + (2 * Math.PI * k) / n;
        placed.set(members[k], { x: center.x + r * Math.cos(angle), y: center.y + r * Math.sin(angle) });
      }
      componentX += 2 * r + gap;
      continue;
    }

    // Interlocking loop: ring the free members outward from the shared anchor.
    const anchor = placed.get(members[primaryIndex]) as Point;
    const cloud = centroid(Array.from(placed.values()));
    let dx = anchor.x - cloud.x;
    let dy = anchor.y - cloud.y;
    const dlen = Math.hypot(dx, dy);
    if (dlen < 1e-6) {
      dx = 1;
      dy = 0;
    } else {
      dx /= dlen;
      dy /= dlen;
    }
    // Push the center outward so the anchor lands exactly on the new ring.
    const center: Point = { x: anchor.x + dx * r, y: anchor.y + dy * r };
    const anchorAngle = Math.atan2(anchor.y - center.y, anchor.x - center.x);

    for (let k = 0; k < n; k++) {
      const ident = members[k];
      if (placed.has(ident)) {
        continue; // shared member stays where it is
      }
      const offset = (((k - primaryIndex) % n) + n) % n; // steps along the cycle from the anchor
      const angle = anchorAngle + (2 * Math.PI * offset) / n;
      placed.set(ident, { x: center.x + r * Math.cos(angle), y: center.y + r * Math.sin(angle) });
    }
  }

  return placed;
}
